import { authClient } from "~/lib/auth-client"
import {
	resolveOrgPermissions,
	resolveTeamPermissions
} from "~/lib/permissionMasks"
import {
	hasOrgPermission as hasOrgPermissionHelper,
	hasTeamPermission as hasTeamPermissionHelper,
	type OrgPermissions as OrgPermissionsType,
	type TeamPermissions as TeamPermissionsType
} from "~/lib/permissions"
import { api } from "~/trpc/react"
import { useOrganization } from "./useOrganization"
import { usePermissionPreview } from "./usePermissionPreview"
import { useTeam } from "./useTeam"

export function usePermission({
	activeTeamId
}: {
	activeTeamId?: string | null
} = {}) {
	const { data: session } = authClient.useSession()
	const { organization } = useOrganization()
	const { team: teamHookTeam } = useTeam()
	const preview = usePermissionPreview()
	const orgMember = organization?.members.find(
		(member) => member.userId === session?.user.id
	)
	const actualOrgInfo = orgMember
		? resolveOrgPermissions(orgMember.roles, organization?.defaultPermissions)
		: { effectivePermissions: 0, isOrgAdmin: false }
	const team = activeTeamId
		? organization?.teams.find((team) => team.id === activeTeamId)
		: teamHookTeam
	const teamMember = team?.teamMembers.find(
		(member) => member.orgMember.userId === session?.user.id
	)
	const actualTeamInfo = teamMember
		? resolveTeamPermissions(teamMember.roles, team?.defaultPermissions)
		: { effectivePermissions: 0, isTeamAdmin: false }

	// Eligibility always uses real membership, never the previewed role.
	const canPreviewOrganization = !!orgMember && actualOrgInfo.isOrgAdmin
	const canPreviewTeam =
		!!orgMember &&
		!!team &&
		(actualOrgInfo.isOrgAdmin || actualTeamInfo.isTeamAdmin)
	const selection = preview?.selection
	const { data: teamRoles } = api.roles.getTeamRoles.useQuery(
		{ teamId: team?.id ?? "" },
		{
			enabled:
				canPreviewTeam &&
				selection?.scope === "team" &&
				selection.scopeId === team?.id
		}
	)
	const { data: orgRoles } = api.roles.getOrgRoles.useQuery(
		{ organizationId: organization?.id ?? "" },
		{
			enabled:
				canPreviewOrganization &&
				selection?.scope === "organization" &&
				selection.scopeId === organization?.id
		}
	)
	const orgRole = (orgRoles ?? organization?.roles)?.find(
		(role) => role.id === selection?.roleId
	)
	const teamRole = teamRoles?.find((role) => role.id === selection?.roleId)
	const isOrgPreview =
		canPreviewOrganization &&
		selection?.scope === "organization" &&
		selection.scopeId === organization?.id &&
		(selection.roleId === "@everyone" || !!orgRole)
	const isTeamPreview =
		canPreviewTeam &&
		selection?.scope === "team" &&
		selection.scopeId === team?.id &&
		(selection.roleId === "@everyone" || !!teamRole)
	const orgInfo = isOrgPreview
		? resolveOrgPermissions(
				orgRole ? [orgRole] : [],
				organization?.defaultPermissions
			)
		: actualOrgInfo
	const teamInfo =
		isTeamPreview || isOrgPreview
			? resolveTeamPermissions(
					isTeamPreview && teamRole ? [teamRole] : [],
					team?.defaultPermissions
				)
			: actualTeamInfo
	const orgPermissions = orgInfo.effectivePermissions
	const teamPermissions = teamInfo.effectivePermissions

	const hasOrgPermission = (permission: OrgPermissionsType) => {
		if (!orgMember) return false
		return hasOrgPermissionHelper(orgPermissions, permission)
	}
	const hasTeamPermission = (permission: TeamPermissionsType) => {
		if (!team) return true
		// A team preview must not inherit the signed-in org admin bypass.
		if (!isTeamPreview && orgInfo.isOrgAdmin) return true
		if (!orgMember || (!teamMember && !isTeamPreview && !isOrgPreview))
			return false
		return hasTeamPermissionHelper(teamPermissions, permission)
	}

	return {
		orgPermissions,
		teamPermissions,
		hasTeamPermission,
		hasOrgPermission,
		teamLoaded: true,
		orgLoaded: !!organization,
		canPreviewOrganization,
		canPreviewTeam,
		previewRole: isTeamPreview
			? (teamRole?.name ?? "@everyone")
			: isOrgPreview
				? (orgRole?.name ?? "@everyone")
				: null,
		isPreviewing: isOrgPreview || isTeamPreview
	}
}
